import Downshift from 'downshift';
import Link from 'next/link';
import {useRouter} from 'next/navigation';
import {useEffect, useId, useState} from 'react';
import { observer } from 'mobx-react-lite';

import {Input} from '@/components/ui/input';
import {useSearchHistory} from '@/libs/hooks/search-history';
import {cn} from '@/libs/utils';
import {ProductDto} from "@/core/application/dtos/ProductDto";
import { useProductStore } from '@/presentation/hooks/useStores';

type SearchBarProps = {
    className?: string;
};

export const SearchBar: React.FC<SearchBarProps> = observer(({className}) => {
    const history = useSearchHistory();
    const router = useRouter();
    const productStore = useProductStore();
    const downshiftId = useId();
    const [searchTerm, setSearchTerm] = useState('');
    const results = ProductDto.fromDomainList(productStore.searchResults).slice(0, 10);

    useEffect(() => {
        const term = searchTerm.trim();
        if (!term) {
            productStore.clearSearchResults();
            return;
        }
        const handle = setTimeout(() => {
            void productStore.searchProducts({ query: term, filters: { limit: 10 } });
        }, 250);
        return () => clearTimeout(handle);
    }, [productStore, searchTerm]);

    return (
        <Downshift
            id={downshiftId}
            onChange={(selection) => {
                if (!selection) return;
                router.push(`/products/${selection.id}`);
                history.addHistory(selection);
            }}
            itemToString={(item) => (item ? item.name : '')}
            onInputValueChange={(e) => setSearchTerm(e || '')}
        >
            {({
                  getInputProps,
                  getItemProps,
                  getMenuProps,
                  highlightedIndex,
                  selectedItem,
                  isOpen,
              }) => (
                <div className={cn('relative', className)}>
                    <div className="  flex w-full flex-col gap-1">
                        <div className="flex gap-0.5">
                            <Input
                                placeholder="What are you looking to buy"
                                className="h-[48px] w-full rounded-full p-4"
                                {...getInputProps()}
                            />
                            {/* <button */}
                            {/*  aria-label="toggle menu" */}
                            {/*  className="px-2" */}
                            {/*  {...getToggleButtonProps()} */}
                            {/* > */}
                            {/*  {isOpen ? <>&#8593;</> : <>&#8595;</>} */}
                            {/* </button> */}
                        </div>
                    </div>

                    <ul
                        className={`absolute top-12 z-10 mt-1 max-h-80 w-full overflow-y-scroll rounded-2xl border border-[#E8E8E8] bg-white p-0 shadow-md ${
                            !(isOpen && results.length) && 'hidden'
                        }`}
                        {...getMenuProps()}
                    >
                        <div className="space-y-2 p-4">
                            <p className="text-[13px] text-muted">Recent Search</p>
                            <div className="flex gap-2 overflow-auto">
                                {history.history.slice(0, 3).map((x) => (
                                    <Link key={x.id} href={`/products/${x.id}`}>
                                        <div className="rounded-xl bg-[#E8E8E8] px-3 py-1 text-sm text-motitoBlack">
                                            <p> {x.name}</p>{' '}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                        {isOpen
                            ? results.map((item, index) => (
                                <li
                                    className={cn(
                                        highlightedIndex === index && 'bg-gray-100',
                                        selectedItem === item && 'font-bold',
                                        'py-2 px-3 shadow-sm flex flex-col cursor-pointer',
                                    )}
                                    key={item.id}
                                    {...getItemProps({
                                        item,
                                        index,
                                    })}
                                >
                                    <div className="flex flex-row items-center gap-2">
                                        <img
                                            className="size-[40px]"
                                            src={item.imageUrl || '/assets/svgs/Icons/Package.svg'}
                                            alt={item.name}
                                        />
                                        <div>
                                            <p>{item.name}</p>
                                            <p className="text-sm text-gray-700">
                                                {item.shopName}
                                            </p>
                                        </div>
                                    </div>
                                </li>
                            ))
                            : null}
                    </ul>
                </div>
            )}
        </Downshift>
    );
});
